import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowUpRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFetch } from "@/hooks/useFetch";
import { useStaff } from "@/context/StaffAuth";
import { dateTime, money, titleCase } from "@/lib/format";
import { ErrorState, PageHeader, SectionCard, Stat, StatusBadge, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function AdminDashboard() {
  const { user } = useStaff();
  const { data, loading, error, reload } = useFetch("/admin/dashboard");

  if (error) return <ErrorState message={error} onRetry={reload} />;

  const cat = data?.catalog;
  const inv = data?.inventory;
  const pur = data?.purchasing;
  const fin = data?.finance;
  const cus = data?.customers;

  return (
    <div data-testid="admin-dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title={`${greeting()}, ${user?.name || "there"}`}
        description={data ? `Snapshot generated ${dateTime(data.generated_at)}. ${data.orders?.note || ""}` : "Loading your operational snapshot…"}
        actions={<Button asChild variant="outline" className="rounded-lg"><Link to="/admin/products/new" data-testid="dashboard-new-product-button">New product <ArrowUpRight className="h-4 w-4 ml-1.5" /></Link></Button>}
      />

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpis">
            {cat && <Stat label="Published products" value={cat.products_by_status?.published || 0} hint={`${cat.products_by_status?.draft || 0} drafts · ${cat.products_by_status?.archived || 0} archived`} to="/admin/products?status=published" testId="kpi-published" />}
            {cat && <Stat label="Active variants" value={cat.active_variants} hint={`${cat.brands} active brands`} to="/admin/products" testId="kpi-variants" />}
            {inv && <Stat label="Units available" value={inv.totals?.available} hint={`${inv.totals?.on_hand} on hand · ${inv.totals?.unavailable} damaged`} to="/admin/inventory" testId="kpi-available" />}
            {inv && <Stat label="Low / out of stock" value={`${inv.low_stock} / ${inv.out_of_stock}`} tone={inv.out_of_stock > 0 ? "warn" : "default"} hint="Active SKUs at or below reorder point" to="/admin/inventory?tab=low-stock" testId="kpi-low-stock" />}
            {pur && <Stat label="Open purchase orders" value={pur.open_purchase_orders} hint={`${pur.draft_purchase_orders} drafts · ${pur.units_incoming} units incoming`} to="/admin/purchase-orders" testId="kpi-open-pos" />}
            {fin && <Stat label="Stock value at cost" value={money(fin.inventory_value_at_cost, fin.currency)} hint={`Estimate · ${fin.skus_missing_cost} SKUs without cost`} testId="kpi-stock-value" />}
            {cus && <Stat label="Registered clients" value={cus.registered} hint={`${cus.new_last_30d} new in 30 days`} to="/admin/customers" testId="kpi-customers" />}
            <Stat label="Orders" value={data?.orders?.count ?? 0} hint="Checkout arrives in Phase 4" testId="kpi-orders" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-8">
            {inv && (
              <SectionCard title="Stock movement · last 14 days" description="Units in vs out of the on-hand bucket" className="lg:col-span-2" testId="dashboard-movement-chart">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={inv.movement_series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barGap={2}>
                      <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                      <Bar dataKey="inbound" name="Inbound" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="outbound" name="Outbound" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            )}
            {inv && (
              <SectionCard title="Ledger integrity" description="Balances reconciled against the immutable movement ledger" testId="dashboard-ledger">
                <div className="flex items-center gap-3">
                  {inv.ledger.ok ? <CheckCircle2 className="h-8 w-8 text-[hsl(var(--accent-forest))]" /> : <AlertTriangle className="h-8 w-8 text-destructive" />}
                  <div>
                    <p className="font-display text-3xl font-medium">{inv.ledger.ok ? "Consistent" : "Attention"}</p>
                    <p className="text-xs text-muted-foreground">{inv.ledger.balances_checked} balances checked</p>
                  </div>
                </div>
                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-secondary p-3"><dt className="eyebrow">Mismatches</dt><dd className="font-semibold tabular mt-1">{inv.ledger.mismatches}</dd></div>
                  <div className="rounded-lg bg-secondary p-3"><dt className="eyebrow">Pending</dt><dd className="font-semibold tabular mt-1">{inv.ledger.pending}</dd></div>
                </dl>
                <Button asChild variant="link" className="px-0 mt-3 text-xs"><Link to="/admin/inventory?tab=ledger" data-testid="dashboard-ledger-link">Open ledger check →</Link></Button>
              </SectionCard>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            {inv && (
              <SectionCard title="Recent stock movements" className="lg:col-span-2" actions={<Button asChild variant="ghost" size="sm"><Link to="/admin/inventory?tab=movements">View all</Link></Button>} testId="dashboard-recent-movements">
                {inv.recent_movements?.length ? (
                  <Table>
                    <TableHeader><TableRow><TableHead className={thCls}>When</TableHead><TableHead className={thCls}>SKU</TableHead><TableHead className={thCls}>Type</TableHead><TableHead className={`${thCls} text-right`}>Qty</TableHead><TableHead className={thCls}>By</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {inv.recent_movements.map((m) => (
                        <TableRow key={m.id} className={rowCls}>
                          <TableCell className={`${tdCls} text-muted-foreground whitespace-nowrap`}>{dateTime(m.created_at)}</TableCell>
                          <TableCell className={`${tdCls} font-medium tabular`}>{m.sku}</TableCell>
                          <TableCell className={tdCls}>{titleCase(m.movement_type)}</TableCell>
                          <TableCell className={`${tdCls} text-right tabular font-semibold ${m.quantity < 0 ? "text-destructive" : "text-[hsl(var(--accent-forest))]"}`}>{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</TableCell>
                          <TableCell className={`${tdCls} text-muted-foreground truncate max-w-[160px]`}>{m.actor_email || "system"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground">No movements recorded yet.</p>}
              </SectionCard>
            )}
            <div className="space-y-6">
              <SectionCard title="Integrations" description="Nothing goes LIVE without verification" testId="dashboard-integrations">
                <ul className="space-y-2.5">
                  {(data?.integrations || []).map((i) => (
                    <li key={i.key} className="flex items-center justify-between gap-3 text-sm"><span className="truncate">{i.name}</span><StatusBadge status={i.mode} /></li>
                  ))}
                </ul>
              </SectionCard>
              {data?.recent_audit && (
                <SectionCard title="Recent audit" actions={<Button asChild variant="ghost" size="sm"><Link to="/admin/audit">Open log</Link></Button>} testId="dashboard-recent-audit">
                  <ul className="space-y-3">
                    {data.recent_audit.map((a) => (
                      <li key={a.id} className="text-xs border-l-2 border-border pl-3">
                        <p className="font-medium text-foreground">{a.action} <span className="text-muted-foreground font-normal">{a.summary ? `· ${a.summary}` : ""}</span></p>
                        <p className="text-muted-foreground">{a.actor_email} · {dateTime(a.created_at)}</p>
                      </li>
                    ))}
                    {!data.recent_audit.length && <li className="text-sm text-muted-foreground">No privileged actions recorded yet.</li>}
                  </ul>
                </SectionCard>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
